/**
 * Produto NA PRÓPRIA LINHA (DEMO-03, telas/ProdutoNaLinha.tsx): PN/SKU e
 * Descrição são dois campos de autocompletar SINCRONIZADOS — buscar por
 * qualquer um preenche o outro e ainda fabricante, pilar, tipo, unidade e moeda
 * (pelo cadastro de Produtos e Serviços). Sem resultado: cadastro rápido
 * (permissão "criar produto"), que antes procura duplicidade. Serviço pode não
 * ter PN/SKU: é achado pela Descrição. Renderiza DUAS células.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { api } from '../../api.ts';
import { useSessao } from '../../sessao.tsx';
import { casaCadastro } from '../../../../backend/src/compartilhado/documentos.ts';
import type { ProdutoRef } from '../../../../backend/src/compartilhado/propostas.ts';
import { ModalProduto } from '../Produtos.tsx';
import { useProposta } from './contexto.tsx';

type Campo = 'pn' | 'descricao';

export function ProdutoNaLinha({
  pn,
  descricao,
  aoEscolher,
  aoDigitar,
  teste,
  rotuloLinha,
  desabilitado,
  obrigatorio,
  detalhe,
}: {
  pn: string;
  descricao: string;
  aoEscolher: (p: ProdutoRef) => void;
  aoDigitar?: (campo: Campo, valor: string) => void;
  teste: string;
  rotuloLinha: string;
  desabilitado?: boolean;
  obrigatorio?: boolean;
  detalhe?: string;
}) {
  const { d, adicionarProduto } = useProposta();
  const { pode } = useSessao();
  const id = useId();
  const [texto, setTexto] = useState<Record<Campo, string>>({ pn, descricao });
  const [ativo, setAtivo] = useState<Campo | null>(null);
  const [realce, setRealce] = useState(0);
  const [digitou, setDigitou] = useState(false);
  const [rapido, setRapido] = useState<Campo | null>(null);
  const [pilares, setPilares] = useState<string[] | null>(null);
  const [pos, setPos] = useState<CSSProperties>({});
  const refs = { pn: useRef<HTMLInputElement>(null), descricao: useRef<HTMLInputElement>(null) };
  useEffect(() => setTexto({ pn, descricao }), [pn, descricao]);

  const q = ativo ? texto[ativo].trim() : '';
  const aberto = Boolean(ativo) && digitou && q.length >= 2;
  const resultados = aberto ? d.produtos.filter((p) => p.situacao === 'Ativo' && casaCadastro(q, ativo === 'pn' ? [p.sku] : [p.nome, p.fabricante])).slice(0, 8) : [];
  // Lista em posição fixa: não é cortada pela rolagem horizontal da tabela; acompanha a rolagem da página.
  useLayoutEffect(() => {
    if (!aberto || !ativo) return;
    const posicionar = () => {
      const r = refs[ativo].current?.getBoundingClientRect();
      if (!r) return;
      const abaixo = window.innerHeight - r.bottom - 8;
      const acima = r.top - 8;
      const largura = Math.min(Math.max(r.width, 440), window.innerWidth - 16);
      const left = Math.max(8, Math.min(r.left, window.innerWidth - largura - 8));
      setPos(abaixo >= 180 || abaixo >= acima ? { position: 'fixed', top: r.bottom + 2, bottom: 'auto', left, right: 'auto', width: largura, maxHeight: Math.min(320, abaixo) } : { position: 'fixed', top: 'auto', bottom: window.innerHeight - r.top + 2, left, right: 'auto', width: largura, maxHeight: Math.min(320, acima) });
    };
    posicionar();
    window.addEventListener('scroll', posicionar, true);
    window.addEventListener('resize', posicionar);
    return () => {
      window.removeEventListener('scroll', posicionar, true);
      window.removeEventListener('resize', posicionar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, ativo, q]);

  const escolher = (p: ProdutoRef) => {
    aoEscolher(p);
    setTexto({ pn: p.sku, descricao: p.nome });
    setAtivo(null);
    setDigitou(false);
    setRealce(0);
  };
  const abrirRapido = async (campo: Campo) => {
    if (!pilares) {
      try {
        setPilares((await api.get<{ pilares: string[] }>('/api/produtos/opcoes')).pilares);
      } catch {
        setPilares([]);
      }
    }
    setRapido(campo);
    setAtivo(null);
  };
  const tecla = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!aberto) return;
    if (e.key === 'ArrowDown') (e.preventDefault(), setRealce((x) => Math.min(x + 1, resultados.length - 1)));
    else if (e.key === 'ArrowUp') (e.preventDefault(), setRealce((x) => Math.max(x - 1, 0)));
    else if (e.key === 'Enter' && resultados[realce]) (e.preventDefault(), escolher(resultados[realce]));
    else if (e.key === 'Escape') (e.preventDefault(), setDigitou(false));
  };
  const entrada = (campo: Campo) => (
    <input
      ref={refs[campo]}
      value={texto[campo]}
      disabled={desabilitado}
      role="combobox"
      aria-expanded={aberto && ativo === campo}
      aria-controls={`${id}-lista`}
      aria-autocomplete="list"
      aria-label={`${campo === 'pn' ? 'PN/SKU' : 'Descrição'} — ${rotuloLinha}`}
      aria-invalid={obrigatorio && campo === 'pn' && !texto.pn.trim() && !texto.descricao.trim() ? true : undefined}
      placeholder={campo === 'pn' ? 'PN/SKU' : 'Descrição do produto ou serviço'}
      className={campo === 'pn' ? 'entrada-pn' : 'entrada-descricao'}
      onFocus={() => setAtivo(campo)}
      onBlur={() => setTimeout(() => (setAtivo((a) => (a === campo ? null : a)), setDigitou(false)), 150)}
      onChange={(e) => {
        setTexto({ ...texto, [campo]: e.target.value });
        setAtivo(campo);
        setDigitou(true);
        setRealce(0);
        aoDigitar?.(campo, e.target.value);
      }}
      onKeyDown={tecla}
      data-teste={`${teste}-${campo}`}
    />
  );
  return (
    <>
      <td className="celula-pn">{entrada('pn')}</td>
      <td className="celula-descricao descricao-produto">
        {entrada('descricao')}
        {detalhe && <div className="texto-secundario">{detalhe}</div>}
        {aberto && (
          <ul className="resultados-produto resultados-linha" id={`${id}-lista`} role="listbox" aria-label={`Produtos encontrados — ${rotuloLinha}`} style={pos} data-teste={`${teste}-resultados`}>
            {resultados.map((p, k) => (
              <li key={p.id} className={k === realce ? 'realce' : undefined}>
                <button type="button" role="option" aria-selected={k === realce} className="botao-link" onMouseDown={(e) => e.preventDefault()} onClick={() => escolher(p)} data-teste={`escolher-${p.sku || p.id}`}>
                  <strong>{p.sku || 'sem PN'}</strong> — {p.nome} <small className="texto-secundario">({p.fabricante} · {p.pilar} · {p.tipoItem} · {p.unidade})</small>
                </button>
              </li>
            ))}
            {resultados.length === 0 && (
              <li className="texto-secundario">
                Nenhum item do catálogo com “{q}”.{' '}
                {pode('criar', 'produto') && (
                  <button type="button" className="botao-link" onMouseDown={(e) => e.preventDefault()} onClick={() => void abrirRapido(ativo!)} data-teste={`${teste}-cadastro-rapido`}>
                    Cadastro rápido
                  </button>
                )}
              </li>
            )}
          </ul>
        )}
        {rapido && pilares && (
          <ModalProduto
            rapido
            pilares={pilares}
            inicial={rapido === 'pn' ? { sku: texto.pn.trim().toUpperCase() } : { nome: texto.descricao.trim() }}
            aoFechar={() => setRapido(null)}
            aoSalvar={(p) => {
              const ref: ProdutoRef = { id: p.id, sku: p.sku, nome: p.nome, fabricante: p.fabricante, unidade: p.unidade, moeda: p.moeda, categoria: p.categoria, pilar: p.pilar, tipoItem: p.tipoItem, situacao: p.situacao };
              adicionarProduto(ref);
              escolher(ref);
              setRapido(null);
            }}
          />
        )}
      </td>
    </>
  );
}
